// src/features/mech/sheetmetal/questions/SurfaceFinish.jsx
import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PerlTextField from "../../../../shared/components/PerlTextField";
import PerlLabelMedium from "../../../../shared/components/PerlLabelMedium";
import PerlSelect from "../../../../shared/components/PerlSelect";

const PLATING_MATERIALS = ["Copper","Nickel", "Tin", "Zinc", "Gold", "Chromium"];
const PAINTING_MATERIALS = ["Powder", "Liquid"];

export default function SurfaceFinish({ value, onChange, onNextStep }) {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#ffffff",
    textPrimary: "#F1F2F3",
    textSecondary: "#9097A1",
  };
  const brandColor =
    theme.custom?.perlColors?.brandColor || theme.palette?.primary?.main || "#ff6034";

  // Parse the value object or initialize with empty strings
  const surfaceFinishValue = value || {
    paintingMaterial: "",
    paintingSurfaceArea: "",
    paintingThickness: "",
    platingMaterial: "",
    platingSurfaceArea: "",
    platingThickness: "",
  };

  const handleFieldChange = (field, newValue) => {
    onChange?.({
      ...surfaceFinishValue,
      [field]: newValue,
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
      {/* Painting Section */}
      <Box>
        <PerlLabelMedium sx={{ mb: 2, display: "block" }}>Painting</PerlLabelMedium>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <PerlSelect
            label="Material"
            value={surfaceFinishValue.paintingMaterial || ""}
            onChange={(e) => handleFieldChange("paintingMaterial", e.target.value)}
            options={PAINTING_MATERIALS}
            fullWidth
            sx={{
              bgcolor: alpha(perlColors.white, 0.02),
              color: perlColors.textPrimary,
              fontSize: "0.9rem",
              borderRadius: 1.5,
              "& fieldset": {
                borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
                borderWidth: "1px",
              },
              "&:hover fieldset": {
                borderColor: `${alpha(brandColor, 0.8)} !important`,
              },
              "&.Mui-focused": {
                "& fieldset": {
                  borderColor: `${brandColor} !important`,
                  borderWidth: "2px !important",
                },
              },
              "&.Mui-focused fieldset": {
                borderColor: `${brandColor} !important`,
                borderWidth: "2px !important",
                boxShadow: `0 0 0 2px ${brandColor}33`,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
                },
                "&:hover fieldset": {
                  borderColor: `${alpha(brandColor, 0.8)} !important`,
                },
                "&.Mui-focused fieldset": {
                  borderColor: `${brandColor} !important`,
                  borderWidth: "2px !important",
                },
              },
              "& .MuiSelect-select": {
                color: !surfaceFinishValue.paintingMaterial
                  ? alpha(perlColors.textSecondary, 0.7)
                  : perlColors.textPrimary,
                fontSize: "0.9rem",
              },
              "& .MuiSvgIcon-root": {
                color: perlColors.textSecondary,
              },
            }}
          />
          <PerlTextField
            label="Surface area (mm²)"
            type="number"
            value={surfaceFinishValue.paintingSurfaceArea || ""}
            onChange={(e) => handleFieldChange("paintingSurfaceArea", e.target.value)}
            InputProps={{
              sx: {
                bgcolor: alpha(perlColors.white, 0.02),
                color: perlColors.textPrimary,
                fontSize: "0.9rem",
                borderRadius: 1.5,
                "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
                "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
                "&.Mui-focused fieldset": {
                  borderColor: brandColor,
                  boxShadow: `0 0 0 2px ${brandColor}33`,
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: perlColors.textSecondary,
                fontSize: "0.85rem",
                letterSpacing: "0.3px",
                "&.Mui-focused": { color: perlColors.textPrimary },
              },
            }}
          />
          <PerlTextField
            label="Thickness (microns)"
            type="number"
            value={surfaceFinishValue.paintingThickness || ""}
            onChange={(e) => handleFieldChange("paintingThickness", e.target.value)}
            InputProps={{
              sx: {
                bgcolor: alpha(perlColors.white, 0.02),
                color: perlColors.textPrimary,
                fontSize: "0.9rem",
                borderRadius: 1.5,
                "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
                "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
                "&.Mui-focused fieldset": {
                  borderColor: brandColor,
                  boxShadow: `0 0 0 2px ${brandColor}33`,
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: perlColors.textSecondary,
                fontSize: "0.85rem",
                letterSpacing: "0.3px",
                "&.Mui-focused": { color: perlColors.textPrimary },
              },
            }}
          />
        </Box>
      </Box>

      {/* Plating Section */}
      <Box>
        <PerlLabelMedium sx={{ mb: 2, display: "block" }}>Plating</PerlLabelMedium>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <PerlSelect
            label="Plating material"
            value={surfaceFinishValue.platingMaterial || ""}
            onChange={(e) => handleFieldChange("platingMaterial", e.target.value)}
            options={PLATING_MATERIALS}
            fullWidth
            sx={{
              bgcolor: alpha(perlColors.white, 0.02),
              color: perlColors.textPrimary,
              fontSize: "0.9rem",
              borderRadius: 1.5,
              "& fieldset": {
                borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
                borderWidth: "1px",
              },
              "&:hover fieldset": {
                borderColor: `${alpha(brandColor, 0.8)} !important`,
              },
              "&.Mui-focused": {
                "& fieldset": {
                  borderColor: `${brandColor} !important`,
                  borderWidth: "2px !important",
                },
              },
              "&.Mui-focused fieldset": {
                borderColor: `${brandColor} !important`,
                borderWidth: "2px !important",
                boxShadow: `0 0 0 2px ${brandColor}33`,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
                },
                "&:hover fieldset": {
                  borderColor: `${alpha(brandColor, 0.8)} !important`,
                },
                "&.Mui-focused fieldset": {
                  borderColor: `${brandColor} !important`,
                  borderWidth: "2px !important",
                },
              },
              "& .MuiSelect-select": {
                color: !surfaceFinishValue.platingMaterial
                  ? alpha(perlColors.textSecondary, 0.7)
                  : perlColors.textPrimary,
                fontSize: "0.9rem",
              },
              "& .MuiSvgIcon-root": {
                color: perlColors.textSecondary,
              },
            }}
          />
          <PerlTextField
            label="Surface area (mm²)"
            type="number"
            value={surfaceFinishValue.platingSurfaceArea || ""}
            onChange={(e) => handleFieldChange("platingSurfaceArea", e.target.value)}
            InputProps={{
              sx: {
                bgcolor: alpha(perlColors.white, 0.02),
                color: perlColors.textPrimary,
                fontSize: "0.9rem",
                borderRadius: 1.5,
                "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
                "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
                "&.Mui-focused fieldset": {
                  borderColor: brandColor,
                  boxShadow: `0 0 0 2px ${brandColor}33`,
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: perlColors.textSecondary,
                fontSize: "0.85rem",
                letterSpacing: "0.3px",
                "&.Mui-focused": { color: perlColors.textPrimary },
              },
            }}
          />
          <PerlTextField
            label="Thickness (microns)"
            type="number"
            value={surfaceFinishValue.platingThickness || ""}
            onChange={(e) => handleFieldChange("platingThickness", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && onNextStep) {
                e.preventDefault();
                onNextStep();
              }
            }}
            InputProps={{
              sx: {
                bgcolor: alpha(perlColors.white, 0.02),
                color: perlColors.textPrimary,
                fontSize: "0.9rem",
                borderRadius: 1.5,
                "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
                "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
                "&.Mui-focused fieldset": {
                  borderColor: brandColor,
                  boxShadow: `0 0 0 2px ${brandColor}33`,
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: perlColors.textSecondary,
                fontSize: "0.85rem",
                letterSpacing: "0.3px",
                "&.Mui-focused": { color: perlColors.textPrimary },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
